import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Prohibit, CheckCircle, ShieldCheck, User, Crown } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";

export default function AdminUsers() {
  const { t, lang } = useI18n();
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [target, setTarget] = useState(null); // {user, block:bool}
  const [busy, setBusy] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const apply = async () => {
    if (!target) return;
    setBusy(target.user.id);
    try {
      await api.patch(`/admin/users/${target.user.id}/block`, { blocked: target.block });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success(
        target.block
          ? lang === "pt" ? "Usuário bloqueado." : "User blocked."
          : lang === "pt" ? "Usuário desbloqueado." : "User unblocked."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "pt" ? "Falha na operação." : "Operation failed."));
    } finally {
      setBusy(null);
      setTarget(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <header className="animate-fade-up">
        <p className="label-caps text-gold">LEXADM · ADMIN</p>
        <h1 className="font-display font-bold text-4xl tracking-tight text-navy mt-2">{t("admin.title")}</h1>
        <p className="text-sm text-slate-500 mt-2">{t("admin.subtitle")}</p>
      </header>

      <div className="mt-8 bg-white border border-slate-200 rounded-lg overflow-hidden animate-fade-up" style={{ animationDelay: "60ms" }}>
        {isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-400">{t("common.loading")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="label-caps px-5 py-3">{t("admin.name")}</th>
                  <th className="label-caps px-5 py-3 hidden md:table-cell">{t("admin.role")}</th>
                  <th className="label-caps px-5 py-3 hidden sm:table-cell">{t("admin.analyses")}</th>
                  <th className="label-caps px-5 py-3">{t("admin.status")}</th>
                  <th className="label-caps px-5 py-3 text-right">{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => {
                  const isMe = u.id === me?.id;
                  const isAdmin = u.role === "admin";
                  return (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span
                            className="flex items-center justify-center w-9 h-9 rounded-full shrink-0"
                            style={{ background: isAdmin ? "#0A1128" : "#EEF2F6", color: isAdmin ? "#D4AF37" : "#64748b" }}
                          >
                            {isAdmin ? <Crown size={16} weight="fill" /> : <User size={16} weight="fill" />}
                          </span>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-navy truncate flex items-center gap-2">
                              {u.name}
                              {isMe && (
                                <span className="text-[10px] font-bold uppercase tracking-widest text-gold bg-gold/10 rounded px-1.5 py-0.5">
                                  {t("admin.you")}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 truncate">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <span className="text-sm text-slate-600 capitalize">{u.role}</span>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <span className="font-display font-bold text-lg text-navy">{u.analyses_count}</span>
                      </td>
                      <td className="px-5 py-4">
                        {u.blocked ? (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded text-[11px] font-bold tracking-widest text-risk-critical bg-risk-critical/10">
                            <Prohibit size={13} weight="bold" /> {t("admin.blocked")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded text-[11px] font-bold tracking-widest text-risk-low bg-risk-low/10">
                            <ShieldCheck size={13} weight="bold" /> {t("admin.active")}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {isMe || isAdmin ? (
                          <span className="text-xs text-slate-300">—</span>
                        ) : u.blocked ? (
                          <button
                            onClick={() => setTarget({ user: u, block: false })}
                            disabled={busy === u.id}
                            data-testid={`unblock-${u.id}`}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-risk-low hover:bg-risk-low/10 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle size={16} weight="bold" /> {t("admin.unblock")}
                          </button>
                        ) : (
                          <button
                            onClick={() => setTarget({ user: u, block: true })}
                            disabled={busy === u.id}
                            data-testid={`block-${u.id}`}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-risk-critical hover:bg-risk-critical/10 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50"
                          >
                            <Prohibit size={16} weight="bold" /> {t("admin.block")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {target?.block ? t("admin.blockTitle") : t("admin.unblockTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {target?.block ? t("admin.blockDesc") : t("admin.unblockDesc")}
              {target && (
                <span className="block mt-2 font-semibold text-navy">{target.user.name} · {target.user.email}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="block-cancel">{t("admin.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={apply}
              data-testid="block-confirm"
              className={target?.block ? "bg-risk-critical hover:bg-risk-critical/90" : "bg-risk-low hover:bg-risk-low/90"}
            >
              {target?.block ? t("admin.block") : t("admin.unblock")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
